import React, { useState, useEffect } from "react";
import { Settings, ChevronLeft, Star } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import FavoriteDrinksModal from "../components/modals/FavoriteDrinksModal";
import { drinksApi } from "../api/drinks";
import type { Drink } from "../types";

interface UserSettingsViewProps {
  onBack: () => void;
  onNotificationClick: () => void;
}

const UserSettingsView: React.FC<UserSettingsViewProps> = ({
  onBack,
  onNotificationClick,
}) => {
  const { mypage } = useMypageContext();
  const [isFavModalOpen, setIsFavModalOpen] = useState(false);
  const [favoriteDrinks, setFavoriteDrinks] = useState<Drink[]>([]);

  useEffect(() => {
    drinksApi
      .getDrinkFavorites()
      .then(setFavoriteDrinks)
      .catch(() => {});
  }, []);

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      {isFavModalOpen && (
        <FavoriteDrinksModal
          isOpen={isFavModalOpen}
          onClose={() => setIsFavModalOpen(false)}
          onSaveSuccess={setFavoriteDrinks}
        />
      )}
      <div className="overflow-y-auto scrollbar-hide flex-1">
        <PremiumHeader
          title={UI_TEXT.userSettings.title}
          icon={Settings}
          theme="emerald"
          onNotificationClick={onNotificationClick}
        />
        <div
          className="px-6 py-6"
          style={{ paddingBottom: "calc(10rem + env(safe-area-inset-bottom))" }}
        >
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-slate-500 text-sm font-bold mb-6 active:opacity-70 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            {UI_TEXT.userSettings.back}
          </button>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                {UI_TEXT.userSettings.loginIdLabel}
              </label>
              <input
                type="text"
                value={mypage?.login_id ?? ""}
                disabled
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500 cursor-not-allowed"
                readOnly
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                {UI_TEXT.userSettings.dartsLiveCardLabel}
              </label>
              <input
                type="text"
                value={mypage?.dartslive_card_name ?? ""}
                disabled
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500 cursor-not-allowed"
                readOnly
              />
            </div>
          </div>

          {/* お気に入りドリンク */}
          <div className="mt-6">
            <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">
              {UI_TEXT.favoriteDrinks.listTitle}
            </h2>

            <button
              onClick={() => setIsFavModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl py-4 shadow-sm active:scale-95 mb-4 transition-all hover:bg-amber-50 hover:text-amber-600"
            >
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              {UI_TEXT.favoriteDrinks.registerButton}
            </button>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {favoriteDrinks.length === 0 ? (
                <p className="px-5 py-5 text-sm text-slate-400 text-center">
                  {UI_TEXT.favoriteDrinks.empty}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {favoriteDrinks.map((item) => (
                    <li
                      key={item.menu_id}
                      className="flex items-center justify-between px-5 py-3"
                    >
                      <span className="text-sm font-semibold text-slate-700">
                        {item.menu_name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400">
                          ¥{item.price.toLocaleString()}
                        </span>
                        <button
                          onClick={() => {
                            const next = favoriteDrinks.filter(
                              (d) => d.menu_id !== item.menu_id,
                            );
                            setFavoriteDrinks(next);
                            drinksApi.createDrinkFavorite(next).catch(() => {
                              setFavoriteDrinks(favoriteDrinks);
                            });
                          }}
                          aria-label="お気に入りから外す"
                          className="active:opacity-60 transition-opacity"
                        >
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsView;
